import React, { useMemo } from 'react';
import ReactFlow, { ReactFlowProvider, Background, MarkerType } from 'reactflow';
import 'reactflow/dist/style.css';
import { resolverPreview } from '../utils/previewVariaveis';

const nodeColors = {
  menu: '#3b82f6',
  mensagem: '#22c55e',
  finalizar: '#6b7280',
  finalizacao: '#6b7280',
  saudacao: '#8b5cf6',
  aguardar: '#f59e0b',
};

/**
 * Renderiza o texto como o WhatsApp mostra: preserva quebras de linha e aplica
 * *negrito*, _itálico_ e ~riscado~. Mesmo tratamento do WhatsAppSimulator, para
 * o balão do fluxo refletir o que o contato vai ver. URLs viram link clicável.
 */
const URL_RE = /(https?:\/\/[^\s]+)/g;

function comLinks(texto, keyBase) {
  // Divide o texto mantendo as URLs como <a> clicável (abre em nova aba).
  return texto.split(URL_RE).map((parte, i) => {
    if (URL_RE.test(parte)) {
      URL_RE.lastIndex = 0; // reset do regex global
      return (
        <a
          key={`${keyBase}-a${i}`}
          href={parte}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ color: '#93c5fd', textDecoration: 'underline', wordBreak: 'break-all' }}
        >
          {parte}
        </a>
      );
    }
    return <span key={`${keyBase}-t${i}`}>{parte}</span>;
  });
}

function formatarWhatsapp(texto) {
  if (!texto) return null;
  return texto.split('\n').map((linha, li) => {
    const partes = linha.split(/([*_~])([^*_~]+)\1/g);
    return (
      <div key={li} style={{ minHeight: linha === '' ? '0.6em' : undefined }}>
        {partes.map((parte, i) => {
          if (i % 3 === 1) return null; // marcador (*,_,~) — já tratado no i+1
          if (i % 3 === 2) {
            const marker = partes[i - 1];
            const conteudo = comLinks(parte, `${li}-${i}`);
            if (marker === '*') return <strong key={i}>{conteudo}</strong>;
            if (marker === '_') return <em key={i}>{conteudo}</em>;
            if (marker === '~') return <s key={i}>{conteudo}</s>;
          }
          return <span key={i}>{comLinks(parte, `${li}-${i}`)}</span>;
        })}
      </div>
    );
  });
}

const FluxoMiniMapInner = ({ etapas, botoesTemplate, darkMode, variaveis = [], valores = {} }) => {
  const { nodes, edges, height } = useMemo(() => {
    if (!etapas?.length) return { nodes: [], edges: [], height: 200 };

    // Montar mapa de etapas por ID
    const etapaMap = {};
    etapas.forEach(e => { etapaMap[e.id] = e; });

    // Posicionar: BFS a partir da primeira etapa
    const positions = {};
    const visited = new Set();
    const queue = [{ id: etapas[0].id, x: 250, y: 30, level: 0 }];
    let maxY = 100;

    const NODE_W = 240;
    const GAP_V = 30;     // espaço entre um balão e o próximo
    const CHARS_POR_LINHA = 38; // ~caracteres por linha (fonte 9.5 em ~216px)

    // Altura real estimada contando as linhas de fato: cada quebra \n é uma
    // linha (inclusive as vazias, que separam parágrafos), e cada linha longa
    // quebra em sub-linhas. Alinhado ao lineHeight 1.25 de 9.5px (~13px/linha).
    const alturaDe = (etapa) => {
      const txt = resolverPreview(etapa.mensagem_automatica || '', variaveis, valores);
      let linhas = 0;
      if (txt) {
        for (const linha of txt.split('\n')) {
          linhas += linha.length === 0 ? 1 : Math.ceil(linha.length / CHARS_POR_LINHA);
        }
      }
      const textos = [etapa.mensagem_automatica, etapa.mensagem_fallback,
        ...((etapa.opcoes_menu || []).map(o => o?.texto))].filter(Boolean).join(' ');
      const temVars = /\{\{\s*\w+\s*\}\}/.test(textos);
      // padding+nome (34) + linhas*13 + chips (20) + folga (8)
      return 34 + linhas * 13 + (temVars ? 20 : 0) + 8;
    };

    // Primeiro: descobrir a estrutura
    const childrenOf = {};
    etapas.forEach(e => {
      const targets = [];
      if (e.opcoes_menu?.length) {
        e.opcoes_menu.forEach(op => {
          if (op.proxima_etapa_id) targets.push(op.proxima_etapa_id);
        });
      } else if (e.proxima_etapa_id) {
        targets.push(e.proxima_etapa_id);
      }
      childrenOf[e.id] = targets;
    });

    const etapaPorId = {};
    etapas.forEach(e => { etapaPorId[e.id] = e; });

    // BFS pra posicionar — o y do filho considera a ALTURA real do pai.
    while (queue.length > 0) {
      const { id, x, y, level } = queue.shift();
      if (visited.has(id)) continue;
      visited.add(id);

      positions[id] = { x, y };
      const alturaPai = alturaDe(etapaPorId[id]);
      if (y + alturaPai + 40 > maxY) maxY = y + alturaPai + 40;

      const children = childrenOf[id] || [];
      const totalWidth = children.length * (NODE_W + 40);
      const startX = x - totalWidth / 2 + (NODE_W + 40) / 2;
      // Do primeiro balão (nível 0) para os filhos, gap bem maior.
      const gap = level === 0 ? GAP_V * 2.9 : GAP_V;
      const yFilho = y + alturaPai + gap;

      children.forEach((childId, i) => {
        if (!visited.has(childId)) {
          const childX = children.length === 1 ? x : startX + i * (NODE_W + 40);
          queue.push({ id: childId, x: childX, y: yFilho, level: level + 1 });
        }
      });
    }

    // Etapas não visitadas (desconectadas)
    etapas.forEach((e, i) => {
      if (!positions[e.id]) {
        positions[e.id] = { x: 250, y: maxY + 50 };
        maxY += alturaDe(e) + GAP_V;
      }
    });

    // Criar nodes
    const ns = etapas.map((etapa) => {
      const color = nodeColors[etapa.tipo_etapa] || '#6b7280';
      const pos = positions[etapa.id] || { x: 250, y: 0 };

      // Conteúdo da etapa para validar o fluxo direto no balão, JÁ com as
      // variáveis substituídas pelos valores da campanha — mostra o que o
      // contato vai receber de fato. Mensagem inteira, sem cortar.
      const msgBruta = etapa.mensagem_automatica || '';
      const trecho = resolverPreview(msgBruta, variaveis, valores);
      // Variáveis {{...}} usadas nesta etapa (mensagem, fallback e opções).
      const textos = [etapa.mensagem_automatica, etapa.mensagem_fallback,
        ...((etapa.opcoes_menu || []).map(o => o?.texto))].filter(Boolean).join(' ');
      const vars = [...new Set((textos.match(/\{\{\s*\w+\s*\}\}/g) || []).map(v => v.replace(/[{}\s]/g, '')))];

      const label = (
        <div style={{ textAlign: 'left', lineHeight: 1.25 }}>
          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: trecho || vars.length ? 4 : 0 }}>
            {etapa.nome || etapa.tipo_etapa}
          </div>
          {trecho && (
            <div style={{ fontSize: 9.5, fontWeight: 400, opacity: 0.92, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              {formatarWhatsapp(trecho)}
            </div>
          )}
          {vars.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 5 }}>
              {vars.map((v) => (
                <span key={v} style={{
                  fontSize: 8.5, fontWeight: 700, padding: '1px 5px', borderRadius: 5,
                  background: 'rgba(255,255,255,0.28)', color: '#fff',
                }}>
                  {`{{${v}}}`}
                </span>
              ))}
            </div>
          )}
        </div>
      );

      return {
        id: etapa.id,
        position: pos,
        data: { label },
        style: {
          background: color,
          color: '#fff',
          border: 'none',
          borderRadius: '10px',
          padding: '10px 12px',
          fontSize: '11px',
          width: 240,
          textAlign: 'left',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        },
      };
    });

    // Criar edges
    const es = [];
    etapas.forEach((etapa) => {
      if (etapa.opcoes_menu?.length) {
        etapa.opcoes_menu.forEach((op, idx) => {
          if (op.proxima_etapa_id) {
            const match = botoesTemplate?.some(btn =>
              btn.text?.toLowerCase().trim() === (op.texto || op.opcao)?.toLowerCase().trim()
            );
            es.push({
              id: `e-${etapa.id}-op${idx}`,
              source: etapa.id,
              target: op.proxima_etapa_id,
              label: op.texto || op.opcao,
              labelStyle: { fontSize: '10px', fontWeight: 600, fill: match ? '#16a34a' : '#dc2626' },
              labelBgStyle: { fill: darkMode ? '#1f1f23' : '#ffffff', fillOpacity: 0.95 },
              labelBgPadding: [6, 4],
              labelBgBorderRadius: 4,
              style: { stroke: match ? '#22c55e' : '#ef4444', strokeWidth: 2 },
              markerEnd: { type: MarkerType.ArrowClosed, color: match ? '#22c55e' : '#ef4444' },
              animated: !match,
            });
          }
        });
      } else if (etapa.proxima_etapa_id) {
        es.push({
          id: `e-${etapa.id}-next`,
          source: etapa.id,
          target: etapa.proxima_etapa_id,
          style: { stroke: darkMode ? '#52525b' : '#9ca3af', strokeWidth: 1.5 },
          markerEnd: { type: MarkerType.ArrowClosed, color: darkMode ? '#52525b' : '#9ca3af' },
        });
      }
    });

    return { nodes: ns, edges: es, height: maxY + 60 };
  }, [etapas, botoesTemplate, darkMode, variaveis, valores]);

  if (!nodes.length) return null;

  return (
    <div style={{ height: Math.min(height, 700), width: '100%' }} className="rounded-lg overflow-hidden">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        zoomOnScroll={false}
        panOnDrag={false}
        preventScrolling={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background color={darkMode ? '#2a2a2e' : '#f0f0f0'} gap={16} size={1} />
      </ReactFlow>
    </div>
  );
};

const FluxoMiniMap = (props) => (
  <ReactFlowProvider>
    <FluxoMiniMapInner {...props} />
  </ReactFlowProvider>
);

export default FluxoMiniMap;
